import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import {
  sendFavoriteMindboxEvent,
  type FavoriteMindboxExperience,
  type FavoriteMindboxPrice,
} from '@experience/mindbox-events';
import { defineStore } from 'pinia';

import {
  addExperienceToFavorite,
  getExperiencesFavoritesList,
  removeExperienceFromFavorite,
  type ExperienceFavoriteItem,
} from '@/shared/api/experience';
import { useLogger } from '@/shared/utils/logger';

// Ограничивает favorite-действия полями общей Mindbox-модели без legacy-полей карточки.
interface FavoriteToggleExperience {
  id: number;
  photos?: FavoriteMindboxExperience['photos'];
  price?: Pick<FavoriteMindboxPrice, 'value' | 'per_group'>;
}

interface State {
  favoritesState: ExperienceFavoriteItem[];

  /**
   * Флаг, предотвращающий конфликты при множественных изменениях в избранном.
   */
  favoritesToggling: boolean;
}

export const useExperienceEntity = defineStore('attraction:experience-entity', {
  state: (): State => ({
    /**
     * Список избранного.
     */
    favoritesState: [] as ExperienceFavoriteItem[],

    /**
     * Флаг, предотвращающий конфликты при множественных изменениях в избранном.
     */
    favoritesToggling: false,
  }),

  actions: {
    logger: useLogger,

    /**
     * Подгружает список избранного (без списка экскурсий), например для страницы статьи.
     */
    async loadFavorites() {
      try {
        const favorites = await getExperiencesFavoritesList();
        this.favoritesState = favorites.data ?? [];
      } catch (error) {
        this.logger().error('could not load favorites list:', error);
      }
    },

    /**
     * Сбрасывает состояние экземпляра сущности.
     */
    reset() {
      this.favoritesState = [];
      this.favoritesToggling = false;
    },

    /**
     * Добавляет экскурсий или тур в избранное.
     *
     * @param experience экскурсия/тур для добавления
     */
    async setFavorite(experience: FavoriteToggleExperience) {
      if (this.favoritesToggling) {
        return;
      }

      const experienceId = experience.id;
      let favoriteItem;

      this.favoritesToggling = true;

      try {
        // Фиксирует исходное пользовательское действие до favorite API для auth и guest.
        sendFavoriteMindboxEvent({
          action: 'add',
          bootstrapData: getBootstrapData(),
          experience,
        });
        favoriteItem = await addExperienceToFavorite({ path: { experienceId } });
      } catch (e) {
        this.favoritesToggling = false;

        throw e;
      }

      if (favoriteItem.data) {
        this.favoritesState = [...this.favoritesState, favoriteItem.data];
      }

      this.favoritesToggling = false;
    },

    /**
     * Удаляет экскурсию или тура из избранного.
     *
     * @param experience экскурсия/тур для удаления
     */
    async unsetFavorite(experience: FavoriteToggleExperience) {
      if (this.favoritesToggling) {
        return;
      }

      const experienceId = experience.id;

      this.favoritesToggling = true;

      try {
        // Фиксирует исходное пользовательское действие до favorite API для auth и guest.
        sendFavoriteMindboxEvent({
          action: 'remove',
          bootstrapData: getBootstrapData(),
          experience,
        });
        await removeExperienceFromFavorite({ path: { experienceId } });
      } catch (e) {
        this.favoritesToggling = false;

        throw e;
      }

      this.favoritesState = this.favoritesState.filter((item) => item.item_id !== experienceId);
      this.favoritesToggling = false;
    },
  },
});
